import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API } from '../App';
import { AlertTriangle, ShieldAlert, CreditCard, X } from 'lucide-react';

const DISMISS_KEY = 'credit_warning_dismissed';

// Persistent banner shown app-wide: amber soft-warning when balance is low,
// red hard-block banner when balance has hit zero.
const CreditWarningBanner = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(() => sessionStorage.getItem(DISMISS_KEY) === '1');

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/credits/status`);
      setStatus(res.data);
    } catch (err) {
      // Ignore — user may not have a company/subscription context yet
    }
  };

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
  };

  if (!status || (!status.warning && !status.blocked)) return null;
  if (status.blocked === false && status.warning && dismissed) return null;

  const isBlocked = status.blocked;

  return (
    <div
      className={`px-6 py-2.5 flex items-center justify-between gap-4 text-sm font-medium ${
        isBlocked ? 'bg-rose-600 text-white' : 'bg-amber-400 text-amber-950'
      }`}
      data-testid={isBlocked ? 'credit-blocked-banner' : 'credit-warning-banner'}
    >
      <div className="flex items-center gap-2 min-w-0">
        {isBlocked ? (
          <ShieldAlert size={16} className="shrink-0" />
        ) : (
          <AlertTriangle size={16} className="shrink-0" />
        )}
        <span className="truncate">
          {isBlocked
            ? `Credits exhausted (${status.balance} left) — AI transcription, AI assessment, and manual audits are blocked until you top up.`
            : `Low credit balance: only ${status.balance} credit${status.balance === 1 ? '' : 's'} left. Top up soon to avoid interruptions.`}
        </span>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button
          onClick={() => navigate('/credits')}
          className={`flex items-center gap-1 px-3 py-1 rounded-md font-semibold transition-colors ${
            isBlocked
              ? 'bg-white text-rose-700 hover:bg-rose-50'
              : 'bg-amber-950/10 text-amber-950 hover:bg-amber-950/20'
          }`}
          data-testid="credit-banner-topup-btn"
        >
          <CreditCard size={13} />
          Top Up
        </button>
        {!isBlocked && (
          <button
            onClick={dismiss}
            className="text-amber-950/70 hover:text-amber-950"
            data-testid="credit-banner-dismiss-btn"
          >
            <X size={15} />
          </button>
        )}
      </div>
    </div>
  );
};

export default CreditWarningBanner;
